import { supabase } from './supabase';
import type { 
  Product, 
  ProductBatch, 
  StockMove, 
  SalesInvoice, 
  SalesItem, 
  MedicationOrder, 
  DispenseEvent,
  UserProfile 
} from './supabase';

export const userProfileService = {
  async getProfile(userId: string) {
    const { data, error } = await supabase
      .from('user_profiles')
      .select('*')
      .eq('id', userId)
      .single();
    
    if (error && error.code !== 'PGRST116') throw error;
    return data as UserProfile | null;
  },

  async createProfile(userId: string, role: 'admin' | 'doctor' | 'pharmacy') {
    const { data, error } = await supabase
      .from('user_profiles')
      .insert([{ id: userId, role }])
      .select()
      .single();
    
    if (error) throw error;
    return data as UserProfile;
  }
};

export const productService = {
  async getAll(search?: string) {
    let query = supabase.from('products').select('*').order('name');
    
    if (search) {
      query = query.or(`name.ilike.%${search}%,generic_name.ilike.%${search}%`);
    }
    
    const { data, error } = await query;
    if (error) throw error;
    return data as Product[];
  },

  async getById(id: string) {
    const { data, error } = await supabase
      .from('products')
      .select('*')
      .eq('id', id)
      .single();
    
    if (error) throw error;
    return data as Product;
  }
};

export const stockService = {
  async getAvailableBatches(productId: string) {
    const { data, error } = await supabase
      .from('product_batches')
      .select(`
        *,
        product:products(*)
      `)
      .eq('product_id', productId)
      .gt('current_quantity', 0)
      .order('expiry_date', { ascending: true });
    
    if (error) throw error;
    return data as ProductBatch[];
  },

  async move(productId: string, batchId: string, quantity: number, type: StockMove['type'], referenceId?: string, referenceType?: string) {
    const { data, error } = await supabase
      .from('stock_moves')
      .insert([{
        product_id: productId,
        batch_id: batchId,
        type,
        quantity: type === 'return' ? quantity : -quantity,
        reference_id: referenceId,
        reference_type: referenceType
      }])
      .select()
      .single();
    
    if (error) throw error;
    return data as StockMove;
  },

  async checkStock(productId: string, requiredQuantity: number) {
    const batches = await this.getAvailableBatches(productId);
    const totalStock = batches.reduce((sum, batch) => sum + batch.current_quantity, 0);
    return totalStock >= requiredQuantity;
  }
};

export const posService = {
  async createSale(saleData: Omit<SalesInvoice, 'id' | 'invoice_number' | 'created_at' | 'updated_at'>, items: Omit<SalesItem, 'id' | 'sales_invoice_id' | 'total' | 'created_at'>[]) {
    const invoiceNumber = `POS-${Date.now()}`;
    
    const { data: invoice, error: invoiceError } = await supabase
      .from('sales_invoices')
      .insert([{ 
        ...saleData, 
        invoice_number: invoiceNumber 
      }])
      .select()
      .single();
    
    if (invoiceError) throw invoiceError;

    for (const item of items) {
      const { error: itemError } = await supabase
        .from('sales_items')
        .insert([{
          ...item,
          sales_invoice_id: invoice.id
        }]);
      
      if (itemError) throw itemError;

      await stockService.move(
        item.product_id,
        item.batch_id,
        item.quantity,
        'sale',
        invoice.id,
        'sales_invoice'
      );
    }

    return await this.getById(invoice.id);
  },

  async getById(id: string) {
    const { data, error } = await supabase
      .from('sales_invoices')
      .select(`
        *,
        items:sales_items(
          *,
          product:products(*),
          batch:product_batches(*)
        )
      `)
      .eq('id', id)
      .single();
    
    if (error) throw error;
    return data as SalesInvoice;
  },

  async processReturn(invoiceId: string, returnItems: { sales_item_id: string; quantity: number }[]) {
    for (const returnItem of returnItems) {
      const { data: salesItem, error } = await supabase
        .from('sales_items')
        .select('*')
        .eq('id', returnItem.sales_item_id)
        .single();
      
      if (error) throw error;

      await stockService.move(
        salesItem.product_id,
        salesItem.batch_id,
        returnItem.quantity,
        'return',
        invoiceId,
        'sales_return'
      );
    }
  }
};

export const ipdService = {
  async getActiveMedicationOrders(patientId: string) {
    const { data, error } = await supabase
      .from('medication_orders')
      .select(`
        *,
        patient:patients(*),
        doctor:doctors(*),
        product:products(*)
      `)
      .eq('patient_id', patientId)
      .eq('status', 'active')
      .order('created_at', { ascending: false });
    
    if (error) throw error;
    return data as MedicationOrder[];
  },

  async dispenseOrder(orderId: string, quantity: number, batchId: string, userId: string) {
    const { data: order, error: orderError } = await supabase
      .from('medication_orders')
      .select('*')
      .eq('id', orderId)
      .single();
    
    if (orderError) throw orderError;

    const { data: batch, error: batchError } = await supabase
      .from('product_batches')
      .select('*')
      .eq('id', batchId)
      .single();
    
    if (batchError) throw batchError;

    const totalCharge = quantity * batch.selling_price;

    const { data: dispenseEvent, error: dispenseError } = await supabase
      .from('dispense_events')
      .insert([{
        medication_order_id: orderId,
        patient_id: order.patient_id,
        product_id: order.product_id,
        batch_id: batchId,
        quantity,
        unit_price: batch.selling_price,
        total_charge: totalCharge,
        dispensed_by: userId
      }])
      .select()
      .single();
    
    if (dispenseError) throw dispenseError;

    const newDispensedQuantity = order.dispensed_quantity + quantity;
    const newStatus = newDispensedQuantity >= order.prescribed_quantity ? 'completed' : 'active';
    
    await supabase
      .from('medication_orders')
      .update({ 
        dispensed_quantity: newDispensedQuantity,
        status: newStatus
      })
      .eq('id', orderId);

    await stockService.move(
      order.product_id,
      batchId,
      quantity,
      'dispense',
      dispenseEvent.id,
      'dispense_event'
    );

    return dispenseEvent;
  },

  async postChargeToBilling(admissionId: string, chargeData: { 
    productId: string; 
    quantity: number; 
    price: number; 
    source: string;
    description?: string;
  }) {
    const { data: invoice, error: invoiceError } = await supabase
      .from('invoices')
      .select('*')
      .eq('reference_id', admissionId)
      .eq('reference_type', 'admission')
      .eq('status', 'draft')
      .single();

    let invoiceId: string;
    
    if (invoiceError && invoiceError.code === 'PGRST116') {
      const { data: admission } = await supabase
        .from('admissions')
        .select('patient_id')
        .eq('id', admissionId)
        .single();
      
      if (!admission) throw new Error('Admission not found');
      
      const { data: newInvoice, error: createError } = await supabase
        .from('invoices')
        .insert({
          patient_id: admission.patient_id,
          invoice_number: `INV-${Date.now()}`,
          reference_id: admissionId,
          reference_type: 'admission',
          status: 'draft'
        })
        .select()
        .single();
      
      if (createError) throw createError;
      invoiceId = newInvoice.id;
    } else if (invoiceError) {
      throw invoiceError;
    } else {
      invoiceId = invoice.id;
    }

    const { error: itemError } = await supabase
      .from('invoice_items')
      .insert({
        invoice_id: invoiceId,
        description: chargeData.description || `${chargeData.source} charge`,
        quantity: chargeData.quantity,
        unit_price: chargeData.price
      });
    
    if (itemError) throw itemError;
  }
};

export const pharmacyReportsService = {
  async getDaySales(date: string) {
    const { data, error } = await supabase
      .from('sales_invoices')
      .select('total')
      .gte('created_at', `${date}T00:00:00`)
      .lt('created_at', `${date}T23:59:59`);
    
    if (error) throw error;
    
    const totalSales = data.reduce((sum, invoice) => sum + invoice.total, 0);
    return { date, totalSales, invoiceCount: data.length };
  },

  async getLowStock(threshold: number = 10) {
    const { data, error } = await supabase
      .from('product_batches')
      .select(`
        *,
        product:products(*)
      `)
      .lt('current_quantity', threshold)
      .order('current_quantity');
    
    if (error) throw error;
    return data as ProductBatch[];
  },

  async getNearExpiry(days: number = 30) {
    const futureDate = new Date();
    futureDate.setDate(futureDate.getDate() + days);
    
    const { data, error } = await supabase
      .from('product_batches')
      .select(`
        *,
        product:products(*)
      `)
      .lt('expiry_date', futureDate.toISOString().split('T')[0])
      .gt('current_quantity', 0)
      .order('expiry_date');
    
    if (error) throw error;
    return data as ProductBatch[];
  }
};
